import type { LapData, AmendedLapAnalysis } from '@/types/telemetry';
import { formatTime, rawSpeed, rawTemp } from '@/lib/format';
import { useI18n } from '@/context/I18nContext';

interface LapInfoProps {
  lap: LapData;
  amendedAnalysis?: AmendedLapAnalysis;
}

interface StatColumn {
  key: string;
  label: string;
  value: string;
  title?: string;
  muted?: boolean;
}

export function LapInfo({ lap, amendedAnalysis }: LapInfoProps) {
  const { t } = useI18n();

  if (!lap) return null;

  const canonicalSectors = amendedAnalysis?.sectors && amendedAnalysis.sectors.length > 0
    ? amendedAnalysis.sectors
    : null;

  const driftEastM = amendedAnalysis?.gpsDriftEastM ?? 0;
  const driftNorthM = amendedAnalysis?.gpsDriftNorthM ?? 0;
  const driftMagM = Math.hypot(driftEastM, driftNorthM);
  const hasGpsDrift = driftMagM >= 0.2;
  const formatSignedMeters = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(1)} m`;

  const columns: StatColumn[] = [
    { key: 'lapTime', label: t('lapInfo.lapTime'), value: formatTime(lap.lapTimeMs) },
    { key: 'maxRpm', label: t('lapInfo.maxRpm'), value: lap.maxRpm ? String(lap.maxRpm) : '—' },
    { key: 'minRpm', label: t('lapInfo.minRpm'), value: lap.minRpm ? String(lap.minRpm) : '—' },
    { key: 'maxSpeed', label: t('lapInfo.maxSpeed'), value: lap.maxSpeedGpsRaw ? rawSpeed(lap.maxSpeedGpsRaw).toFixed(1) + ' km/h' : '—' },
    { key: 'maxTemp', label: t('lapInfo.maxTemp'), value: lap.maxTemp1Raw ? rawTemp(lap.maxTemp1Raw).toFixed(1) + ' °C' : '—' },
  ];

  if (canonicalSectors) {
    canonicalSectors.forEach((sec, i) => {
      columns.push({
        key: `canon-sec-${sec.sectorNumber || i}`,
        label: sec.name || `S${sec.sectorNumber || i + 1}`,
        value: formatTime(sec.durationMs),
        title: `${sec.minSpeedKmh.toFixed(1)}–${sec.maxSpeedKmh.toFixed(1)} km/h`,
      });
    });
  }

  if (hasGpsDrift) {
    columns.push({
      key: 'gpsOffset',
      label: t('lapInfo.gpsOffset'),
      value: `${driftMagM.toFixed(1)} m`,
      title: t('lapInfo.gpsOffsetTooltip', {
        east: formatSignedMeters(driftEastM),
        north: formatSignedMeters(driftNorthM),
      }),
      muted: true,
    });
  }

  return (
    <div className="card p-0 overflow-hidden" data-testid="lap-info-table">
      <div
        tabIndex={0}
        role="region"
        aria-label={t('lapInfo.lapTime')}
        className="overflow-x-auto"
      >
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="divider">
              {columns.map((col, idx) => (
                <th
                  key={col.key}
                  scope="col"
                  className={`py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted whitespace-nowrap ${
                    idx === 0 ? 'text-left' : 'text-right'
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="hover:bg-bg-hover/60 transition-colors">
              {columns.map((col, idx) => (
                <td
                  key={col.key}
                  title={col.title}
                  className={`py-2.5 px-4 mono tabular-nums whitespace-nowrap ${
                    idx === 0
                      ? 'text-left font-semibold text-text-primary'
                      : col.muted
                      ? 'text-right font-medium text-text-secondary'
                      : 'text-right font-semibold text-text-primary'
                  }`}
                >
                  {col.value}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
